import { ArrowFirst, ArrowLast, ArrowLeft, ArrowRight } from '@jsluna/icons';
import classnames from 'classnames';
import { filterProps } from '@jsluna/utils';
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

var PaginationNav = function PaginationNav(_ref) {
  var className = _ref.className,
      children = _ref.children,
      label = _ref.label,
      showFirstAndLast = _ref.showFirstAndLast,
      showPages = _ref.showPages,
      fullWidth = _ref.fullWidth;
  return /*#__PURE__*/React.createElement("nav", {
    className: classnames(className, 'ln-c-pagination', showFirstAndLast && 'ln-c-pagination--first-last', showPages && 'ln-c-pagination--pages', fullWidth && 'ln-c-pagination--full'),
    "aria-label": label
  }, /*#__PURE__*/React.createElement("ul", {
    className: "ln-c-pagination__list"
  }, children));
};

PaginationNav.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  label: PropTypes.string,
  showFirstAndLast: PropTypes.bool,
  showPages: PropTypes.bool,
  fullWidth: PropTypes.bool
};
PaginationNav.defaultProps = {
  className: undefined,
  children: undefined,
  label: 'Page navigation',
  showFirstAndLast: false,
  showPages: false,
  fullWidth: false
};
PaginationNav.displayName = 'PaginationNav';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var _LINK_REL;

var POSITION_FIRST_CHILD = 'first-child';
var POSITION_LAST_CHILD = 'last-child';
var POSITION_ONLY_CHILD = 'only-child';
var TYPE_PREVIOUS = 'previous';
var TYPE_NEXT = 'next';
var TYPE_FIRST = 'first';
var TYPE_LAST = 'last';
var TYPE_PAGE = 'page';
var ALL_TYPES = [TYPE_PREVIOUS, TYPE_NEXT, TYPE_FIRST, TYPE_LAST, TYPE_PAGE];
var LINK_REL = (_LINK_REL = {}, _LINK_REL[TYPE_PREVIOUS] = 'prev', _LINK_REL[TYPE_NEXT] = 'next', _LINK_REL[TYPE_FIRST] = 'first', _LINK_REL[TYPE_LAST] = 'last', _LINK_REL);
var ELLIPSIS = '...';

var PaginationItem = function PaginationItem(_ref) {
  var children = _ref.children,
      type = _ref.type,
      className = _ref.className,
      page = _ref.page,
      onClick = _ref.onClick,
      href = _ref.href,
      disabled = _ref.disabled,
      element = _ref.element,
      current = _ref.current,
      label = _ref.label,
      rest = _objectWithoutPropertiesLoose(_ref, ["children", "type", "className", "page", "onClick", "href", "disabled", "element", "current", "label"]);

  var Link = element;
  var linkProps = Object.assign({}, rest, {
    className: classnames('ln-c-pagination__link', className, {
      'is-disabled': disabled
    }),
    onClick: onClick ? function (e) {
      return onClick(page, e);
    } : undefined,
    disabled: disabled,
    page: page,
    'aria-label': label,
    'aria-current': current ? true : undefined
  });

  if (disabled) {
    Link = 'button';
    linkProps.type = 'button';
    linkProps['aria-disabled'] = true;
  }

  if (Link === 'a') {
    linkProps.href = href;
  }

  var listItemClassName = classnames('ln-c-pagination__item', "ln-c-pagination__item--" + type, current && 'is-current');
  return /*#__PURE__*/React.createElement("li", {
    className: listItemClassName
  }, /*#__PURE__*/React.createElement(Link, filterProps(linkProps, ['page']), children));
};

PaginationItem.displayName = 'PaginationItem';
PaginationItem.propTypes = {
  children: PropTypes.node.isRequired,
  type: PropTypes.oneOf(ALL_TYPES).isRequired,
  className: PropTypes.string,
  page: PropTypes.number,
  current: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  href: PropTypes.string,
  disabled: PropTypes.bool,
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func])
};
PaginationItem.defaultProps = {
  className: undefined,
  page: undefined,
  current: false,
  onClick: undefined,
  href: undefined,
  disabled: false,
  element: 'a'
};

var _TYPES;

var TYPES = (_TYPES = {}, _TYPES[TYPE_PREVIOUS] = {
  label: 'Previous',
  icon: ArrowLeft,
  rel: LINK_REL[TYPE_PREVIOUS]
}, _TYPES[TYPE_NEXT] = {
  label: 'Next',
  icon: ArrowRight,
  rel: LINK_REL[TYPE_NEXT]
}, _TYPES[TYPE_FIRST] = {
  label: 'First',
  icon: ArrowFirst,
  rel: LINK_REL[TYPE_FIRST]
}, _TYPES[TYPE_LAST] = {
  label: 'Last',
  icon: ArrowLast,
  rel: LINK_REL[TYPE_LAST]
}, _TYPES);

var buildLabel = function buildLabel(type) {
  return TYPES[type].label + " page";
};

var PaginationControl = function PaginationControl(_ref) {
  var type = _ref.type,
      rest = _objectWithoutPropertiesLoose(_ref, ["type"]);

  var typeProps = TYPES[type];
  var Icon = typeProps.icon;
  return /*#__PURE__*/React.createElement(PaginationItem, _extends({}, rest, {
    type: type,
    label: buildLabel(type),
    rel: typeProps.rel
  }), /*#__PURE__*/React.createElement(Icon, {
    "aria-hidden": true
  }));
};

PaginationControl.displayName = 'PaginationControl';
PaginationControl.propTypes = {
  type: PropTypes.oneOf(ALL_TYPES).isRequired
};

var PaginationPrevious = function PaginationPrevious(props) {
  return /*#__PURE__*/React.createElement(PaginationControl, _extends({}, props, {
    type: TYPE_PREVIOUS
  }));
};

PaginationPrevious.displayName = 'PaginationPrevious';

var PaginationNext = function PaginationNext(props) {
  return /*#__PURE__*/React.createElement(PaginationControl, _extends({}, props, {
    type: TYPE_NEXT
  }));
};

PaginationNext.displayName = 'PaginationNext';

var PaginationFirst = function PaginationFirst(props) {
  return /*#__PURE__*/React.createElement(PaginationControl, _extends({}, props, {
    type: TYPE_FIRST
  }));
};

PaginationFirst.displayName = 'PaginationFirst';

var PaginationLast = function PaginationLast(props) {
  return /*#__PURE__*/React.createElement(PaginationControl, _extends({}, props, {
    type: TYPE_LAST
  }));
};

PaginationLast.displayName = 'PaginationLast';

var range = function range(from, to) {
  var o = [];

  for (var i = from; i <= to; i += 1) {
    o.push(i);
  }

  return o;
};

var calcPages = function calcPages(current, total, plusOrMinus) {
  var core = 1 + 2 * plusOrMinus;
  var numOfSlots = core + 2 + 2;
  /* 2 ellipsis + (first + last) */
  // If it fits, I sits

  if (total <= numOfSlots) {
    return range(1, total);
  } // Pages


  var offset = plusOrMinus + 1 + 1;
  var middle = Math.min(Math.max(current, 1 + offset), total - offset);
  var pages = range(middle - plusOrMinus, middle + plusOrMinus); // Beginning

  if (pages[0] !== 1) {
    pages.splice(0, 0, 1);
    pages.splice(1, 0, pages[1] === 3 ? 2 : ELLIPSIS);
  } // End


  if (pages[pages.length - 1] !== total) {
    pages.push(total);
    pages.splice(-1, 0, pages[pages.length - 2] === total - 2 ? total - 1 : ELLIPSIS);
  }

  return pages;
};

var getPages = function getPages(current, total, plusOrMinus) {
  var pages = calcPages(current, total, plusOrMinus);

  var getPosition = function getPosition(prev, next) {
    var p = prev && prev !== ELLIPSIS;
    var n = next && next !== ELLIPSIS;
    if (!p && !n) return POSITION_ONLY_CHILD;
    if (!p) return POSITION_FIRST_CHILD;
    if (!n) return POSITION_LAST_CHILD;
    return undefined;
  };

  var o = [];
  var prev;
  var next;

  for (var i = 0; i < pages.length; i += 1) {
    var page = pages[i];
    next = i < pages.length - 1 ? pages[i + 1] : undefined;
    o.push({
      page: page,
      position: getPosition(prev, next)
    });
    prev = page;
  }

  return o;
}; // https://stackoverflow.com/a/31615643/186965


var calcOrdinal = function calcOrdinal(n) {
  var s = ['th', 'st', 'nd', 'rd'];
  var v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

var buildLabel$1 = function buildLabel(page, current) {
  return "" + (current ? 'Current page, ' : '') + calcOrdinal(page) + " page";
};

var PaginationPage = function PaginationPage(_ref) {
  var page = _ref.page,
      current = _ref.current,
      rest = _objectWithoutPropertiesLoose(_ref, ["page", "current"]);

  return /*#__PURE__*/React.createElement(PaginationItem, _extends({}, rest, {
    type: TYPE_PAGE,
    page: page,
    current: current,
    label: buildLabel$1(page, current)
  }), page);
};

PaginationPage.propTypes = {
  page: PropTypes.number,
  current: PropTypes.bool
};
PaginationPage.defaultProps = {
  page: undefined,
  current: false
};
PaginationPage.displayName = 'PaginationPage';

var PaginationEllipsis = function PaginationEllipsis() {
  return /*#__PURE__*/React.createElement("li", {
    className: "ln-c-pagination__item ln-c-pagination__item--ellipsis",
    "aria-hidden": true
  }, "\u2026");
};

PaginationEllipsis.displayName = 'PaginationEllipsis';

var PaginationInfo = function PaginationInfo(_ref) {
  var children = _ref.children,
      current = _ref.current,
      total = _ref.total;
  return /*#__PURE__*/React.createElement("li", {
    className: "ln-c-pagination__info",
    "aria-live": "polite",
    "aria-atomic": "true"
  }, children || /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: "ln-u-visually-hidden@max-xs"
  }, "Page\xA0"), current, " of ", total));
};

PaginationInfo.displayName = 'PaginationInfo';
PaginationInfo.propTypes = {
  children: PropTypes.node,
  current: PropTypes.number,
  total: PropTypes.number
};
PaginationInfo.defaultProps = {
  children: undefined,
  current: undefined,
  total: undefined
};
/* eslint-disable react/no-array-index-key */

var getLinkRel = function getLinkRel(_ref) {
  var page = _ref.page,
      current = _ref.current,
      total = _ref.total;

  if (page === 1) {
    return LINK_REL[TYPE_FIRST];
  }

  if (page === total) {
    return LINK_REL[TYPE_LAST];
  }

  if (page === current - 1) {
    return LINK_REL[TYPE_PREVIOUS];
  }

  if (page === current + 1) {
    return LINK_REL[TYPE_NEXT];
  }

  return undefined;
};

var PaginationPages = function PaginationPages(props) {
  var onChange = props.onChange,
      current = props.current,
      total = props.total,
      createHref = props.createHref,
      element = props.element,
      range = props.range;
  return getPages(current, total, range).map(function (_ref2, index) {
    var page = _ref2.page;
    return page === ELLIPSIS ? /*#__PURE__*/React.createElement(PaginationEllipsis, {
      key: index
    }) : /*#__PURE__*/React.createElement(PaginationPage, {
      key: index,
      page: page,
      onClick: onChange,
      href: createHref(page),
      current: current === page,
      element: element,
      rel: getLinkRel({
        page: page,
        current: current,
        total: total
      })
    });
  });
};

PaginationPages.propTypes = {
  range: PropTypes.number.isRequired,
  current: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onChange: PropTypes.func,
  createHref: PropTypes.func,
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func])
};
PaginationPages.defaultProps = {
  onChange: undefined,
  createHref: undefined
};

var Pagination = function Pagination(_ref) {
  var className = _ref.className,
      current = _ref.current,
      total = _ref.total,
      onChange = _ref.onChange,
      linkElement = _ref.linkElement,
      createHref = _ref.createHref,
      showFirstAndLast = _ref.showFirstAndLast,
      showPages = _ref.showPages,
      pageRange = _ref.pageRange,
      fullWidth = _ref.fullWidth;
  return /*#__PURE__*/React.createElement(PaginationNav, {
    className: className,
    showFirstAndLast: showFirstAndLast,
    showPages: showPages,
    fullWidth: fullWidth
  }, showFirstAndLast && /*#__PURE__*/React.createElement(PaginationFirst, {
    disabled: current <= 1,
    page: 1,
    element: linkElement,
    onClick: onChange,
    href: createHref(1)
  }), /*#__PURE__*/React.createElement(PaginationPrevious, {
    disabled: current <= 1,
    page: current - 1,
    element: linkElement,
    onClick: onChange,
    href: createHref(current - 1)
  }), /*#__PURE__*/React.createElement(PaginationInfo, {
    current: current,
    total: total
  }), showPages && /*#__PURE__*/React.createElement(PaginationPages, {
    range: pageRange,
    current: current,
    total: total,
    element: linkElement,
    onChange: onChange,
    createHref: createHref
  }), /*#__PURE__*/React.createElement(PaginationNext, {
    disabled: current >= total,
    page: current + 1,
    element: linkElement,
    onClick: onChange,
    href: createHref(current + 1)
  }), showFirstAndLast && /*#__PURE__*/React.createElement(PaginationLast, {
    disabled: current >= total,
    page: total,
    element: linkElement,
    onClick: onChange,
    href: createHref(total)
  }));
};

Pagination.propTypes = {
  /** Custom CSS classes */
  className: PropTypes.string,

  /** Currently selected page */
  current: PropTypes.number.isRequired,

  /** Total number of pages */
  total: PropTypes.number.isRequired,

  /* Called when current page number changes */
  onChange: PropTypes.func.isRequired,

  /** Element to be used as link */
  linkElement: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func]),

  /** Function used to generate page link `href`s (page number passed as argument) */
  createHref: PropTypes.func,

  /** Show first and last page links i.e. `|<` and `>|` */
  showFirstAndLast: PropTypes.bool,

  /** Show page number buttons/links */
  showPages: PropTypes.bool,

  /** Number of page buttons/links displayed each side of the current page */
  pageRange: PropTypes.number,

  /** Resize to 100% width of the parent */
  fullWidth: PropTypes.bool
};
Pagination.defaultProps = {
  className: undefined,
  linkElement: 'a',
  createHref: function createHref(page) {
    return "#" + page;
  },
  showFirstAndLast: false,
  showPages: false,
  pageRange: 1,
  fullWidth: false
};
Pagination.displayName = 'Pagination';
export { PaginationNav, PaginationPrevious, PaginationNext, PaginationFirst, PaginationLast, PaginationPage, PaginationEllipsis, PaginationInfo, Pagination };